// ===============================
// src/pages/InvitaAmico.jsx
// ===============================
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { buildLoginUrl } from "../utils/authRedirect";
import toast from "react-hot-toast";
import { getProfilo } from "../Admin/adminApi";
import "./css/InvitaAmico.css";

function Icon({ name }) {
  if (name === "share") {
    return (
      <svg viewBox="0 0 24 24" className="ia-icon" aria-hidden="true">
        <circle
          cx="18"
          cy="5"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <circle
          cx="6"
          cy="12"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <circle
          cx="18"
          cy="19"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M8.7 10.7l6.6-4.4M8.7 13.3l6.6 4.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (name === "copy") {
    return (
      <svg viewBox="0 0 24 24" className="ia-icon" aria-hidden="true">
        <rect
          x="8"
          y="7"
          width="11"
          height="14"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M5 16H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return null;
}

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function InvitaAmico() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const [referral, setReferral] = useState({
    code: "",
    link: "",
    active: true,
    error: "",
  });

  const [stats, setStats] = useState({
    friendsRegistered: 0,
    friendsWithOrders: 0,
  });

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!token) {
        // dopo il login si torna qui (?redirect=)
        navigate(buildLoginUrl(location), { replace: true });
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };

      try {
        setLoading(true);

        const [profileData, referralResult] = await Promise.allSettled([
          getProfilo(token),
          fetch(`${API_BASE}/api/referral/me`, { headers }),
        ]);

        if (!alive) return;

        if (profileData.status === "fulfilled") {
          setMe(profileData.value || null);
        } else {
          console.error(profileData.reason);
          toast.error("Errore caricamento profilo");
          setMe(null);
        }

        if (referralResult.status === "fulfilled") {
          const res = referralResult.value;
          const data = await res.json().catch(() => null);

          if (!alive) return;

          if (!res.ok || data?.ok === false) {
            throw new Error(data?.error || "Errore caricamento referral");
          }

          setReferral({
            code: data?.code || "",
            link: data?.link || "",
            active: !!data?.active,
            error: "",
          });
        } else {
          throw referralResult.reason;
        }

        try {
          const statsRes = await fetch(`${API_BASE}/api/referral/me/stats`, {
            headers,
          });

          if (!alive) return;

          if (statsRes.ok) {
            const s = await statsRes.json().catch(() => null);
            if (s && s.ok !== false) {
              setStats({
                friendsRegistered: Number(s.friends_registered ?? 0),
                friendsWithOrders: Number(s.friends_with_orders ?? 0),
              });
            }
          }
        } catch (e) {
          if (!alive) return;
          console.error("Errore stats referral:", e);
          setStats({ friendsRegistered: 0, friendsWithOrders: 0 });
        }
      } catch (e) {
        if (!alive) return;
        console.error(e);
        setReferral({
          code: "",
          link: "",
          active: false,
          error: e?.message || "Errore caricamento referral",
        });
        toast.error("Errore caricamento invita un amico");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const role = String(me?.role || "").toLowerCase();
  const isFornitore = role === "fornitore";

  const inviteCode = String(referral.code || "").trim();
  const inviteLink = String(referral.link || "").trim();

  const safeRegisteredCount = Number.isFinite(stats.friendsRegistered)
    ? stats.friendsRegistered
    : 0;

  const safeOrderedCount = Number.isFinite(stats.friendsWithOrders)
    ? stats.friendsWithOrders
    : 0;

  const canUseReferral = referral.active && !referral.error && !!inviteCode;

  const copyText = async (value, label) => {
    if (!value) {
      toast.error(`${label} non disponibile`);
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiato`);
    } catch (e) {
      console.error(e);
      toast.error("Impossibile copiare negli appunti");
    }
  };

  const shareText = async (value) => {
    if (!value) {
      toast.error("Link non disponibile");
      return;
    }

    const text = inviteCode
      ? `Ti regalo uno sconto sul primo ordine! Usa il mio codice ${inviteCode}: ${value}`
      : `Ti regalo uno sconto sul primo ordine! ${value}`;

    try {
      if (navigator.share) {
        await navigator.share({ title: "Invito StirUp", text, url: value });
        return;
      }

      await navigator.clipboard.writeText(text);
      toast.success("Testo invito copiato");
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error(e);
      toast.error("Impossibile condividere");
    }
  };

  if (loading) {
    return (
      <main className="ia-page">
        <div className="ia-skel ia-skel-hero" />
        <div className="ia-skel" />
        <div className="ia-skel" />
      </main>
    );
  }

  if (isFornitore) {
    return (
      <main className="ia-page">
        <section className="ia-card">
          <h1>Presenta un amico</h1>
          <p>Questa sezione è disponibile per i clienti.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="ia-page">
      <section className="ia-hero" aria-label="Presenta un amico">
        <img
          src="/invita_amico.jpg"
          alt="Presenta un amico Stirup"
          className="ia-hero-img"
        />
      </section>

      <section className="ia-card ia-intro-card">
        <h1>Presenta un amico</h1>

        <p>
          Condividi Stirup con chi vuoi e aiutalo a liberarsi dallo stiro, una
          delle attività domestiche più inutilmente punitive mai inventate
          dall&rsquo;umanità 😅
        </p>

        <p>
          Invita un amico a registrarsi: quando userà il tuo codice, potrà
          ricevere un vantaggio sul primo ordine.
        </p>

        <p>Più amici inviti, più vantaggi puoi accumulare nel tuo account.</p>
      </section>

      <section className="ia-card ia-share-card" aria-label="Codice invito">
        <div className="ia-stats-row">
          <span className="ia-pill ia-pill-purple">
            <span className="ia-pill-icon">👥</span>
            Amici registrati <strong>{safeRegisteredCount}</strong>
          </span>

          <span className="ia-pill ia-pill-green">
            <span className="ia-pill-icon">✅</span>
            Con almeno un ordine <strong>{safeOrderedCount}</strong>
          </span>
        </div>

        {referral.error && (
          <div className="ia-referral-error" role="alert">
            ⚠️ {referral.error}
          </div>
        )}

        <div className="ia-fields">
          <div className="ia-field-row">
            <label htmlFor="invite-code">Codice:</label>
            <input
              id="invite-code"
              value={inviteCode || "—"}
              readOnly
              aria-label="Codice invito"
            />
            <button
              type="button"
              className="ia-action-btn"
              onClick={() => shareText(inviteLink || inviteCode)}
              aria-label="Condividi codice invito"
              title="Condividi"
              disabled={!canUseReferral}
            >
              <Icon name="share" />
            </button>
            <button
              type="button"
              className="ia-action-btn"
              onClick={() => copyText(inviteCode, "Codice")}
              aria-label="Copia codice invito"
              title="Copia"
              disabled={!canUseReferral}
            >
              <Icon name="copy" />
            </button>
          </div>

          <div className="ia-field-row">
            <label htmlFor="invite-link">Link personale:</label>
            <input
              id="invite-link"
              value={inviteLink}
              readOnly
              aria-label="Link personale"
            />
            <button
              type="button"
              className="ia-action-btn"
              onClick={() => shareText(inviteLink)}
              aria-label="Condividi link personale"
              title="Condividi"
              disabled={!canUseReferral || !inviteLink}
            >
              <Icon name="share" />
            </button>
            <button
              type="button"
              className="ia-action-btn"
              onClick={() => copyText(inviteLink, "Link")}
              aria-label="Copia link personale"
              title="Copia"
              disabled={!canUseReferral || !inviteLink}
            >
              <Icon name="copy" />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
